import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save, Upload, ClipboardList, Lock, Unlock } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core'
import api from '../../../core/services/api'
import { Toast } from '../../../components/Toast'
import type { SchedaTemplateDetail, SchemaField, FieldType, EditPolicy } from '../../../types/schede'
import { createDefaultField, FIELD_PALETTE } from '../../../types/schede'
import FieldPalette from '../../../components/schede/builder/FieldPalette'
import BuilderCanvas from '../../../components/schede/builder/BuilderCanvas'
import PropertyPanel from '../../../components/schede/builder/PropertyPanel'

export default function SchedaBuilderPage() {
  const { templateId } = useParams<{ templateId: string }>()
  const navigate = useNavigate()

  const [template, setTemplate] = useState<SchedaTemplateDetail | null>(null)
  const [fields, setFields] = useState<SchemaField[]>([])
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null)
  const [isDirty, setIsDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [editPolicy, setEditPolicy] = useState<EditPolicy>('editable')
  const [activeDragType, setActiveDragType] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  )

  useEffect(() => {
    if (!templateId) return
    loadTemplate()
  }, [templateId])

  const loadTemplate = async () => {
    try {
      const res = await api.get(`/v1/schede-dinamiche/templates/${templateId}`)
      setTemplate(res.data)
      setFields(res.data.schema_json || [])
      setEditPolicy(res.data.edit_policy || 'editable')
    } catch {
      setToast({ message: 'Errore caricamento template', type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const selectedField = fields.find(f => f.id === selectedFieldId) || null

  // ---- Field operations ----

  const addField = useCallback((type: SchemaField['type'], position?: number) => {
    const ordine = position ?? fields.length
    const newField = createDefaultField(type, ordine)
    const updated = [...fields]
    updated.splice(ordine, 0, newField)
    updated.forEach((f, i) => { f.ordine = i })
    setFields(updated)
    setSelectedFieldId(newField.id)
    setIsDirty(true)
  }, [fields])

  const updateField = useCallback((fieldId: string, updates: Partial<SchemaField>) => {
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f))
    setIsDirty(true)
  }, [])

  const removeField = useCallback((fieldId: string) => {
    setFields(prev => {
      const updated = prev.filter(f => f.id !== fieldId)
      updated.forEach((f, i) => { f.ordine = i })
      return updated
    })
    if (selectedFieldId === fieldId) setSelectedFieldId(null)
    setIsDirty(true)
  }, [selectedFieldId])

  const reorderFields = useCallback((oldIndex: number, newIndex: number) => {
    setFields(prev => {
      const updated = [...prev]
      const [moved] = updated.splice(oldIndex, 1)
      updated.splice(newIndex, 0, moved)
      updated.forEach((f, i) => { f.ordine = i })
      return updated
    })
    setIsDirty(true)
  }, [])

  // ---- DnD handler: gestisce sia drop da palette che riordino canvas ----

  const handleDragStart = useCallback((event: { active: { data: { current?: any } } }) => {
    const data = event.active.data.current
    if (data?.source === 'palette') {
      setActiveDragType(data.type)
    }
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragType(null)
    const { active, over } = event

    if (!over) return

    const activeData = active.data.current

    // Drop dalla palette → crea nuovo campo
    if (activeData?.source === 'palette') {
      const fieldType = activeData.type as FieldType

      // Determina posizione di inserimento
      let position = fields.length // default: in fondo
      const overId = String(over.id)

      if (overId !== 'canvas-droppable') {
        // Droppato su un campo esistente → inserisci a quella posizione
        const overIndex = fields.findIndex(f => f.id === overId)
        if (overIndex !== -1) {
          position = overIndex + 1
        }
      }

      addField(fieldType, position)
      return
    }

    // Riordino campi nel canvas
    if (active.id !== over.id) {
      const oldIndex = fields.findIndex(f => f.id === active.id)
      const newIndex = fields.findIndex(f => f.id === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        reorderFields(oldIndex, newIndex)
      }
    }
  }, [fields, addField, reorderFields])

  // ---- Save / Publish ----

  const handleSave = async () => {
    if (!templateId) return
    setSaving(true)
    try {
      await api.put(`/v1/schede-dinamiche/templates/${templateId}`, {
        schema_json: fields,
        edit_policy: editPolicy,
      })
      setIsDirty(false)
      setToast({ message: 'Template salvato', type: 'success' })
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore salvataggio', type: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handlePublish = async () => {
    if (!templateId) return
    if (isDirty) {
      try {
        await api.put(`/v1/schede-dinamiche/templates/${templateId}`, {
          schema_json: fields,
          edit_policy: editPolicy,
        })
        setIsDirty(false)
      } catch (err: any) {
        setToast({ message: err.response?.data?.detail || 'Errore salvataggio', type: 'error' })
        return
      }
    }
    setPublishing(true)
    try {
      const res = await api.post(`/v1/schede-dinamiche/templates/${templateId}/pubblica`)
      setTemplate(res.data)
      setToast({ message: `Pubblicata versione ${res.data.versione - 1}`, type: 'success' })
    } catch (err: any) {
      setToast({ message: err.response?.data?.detail || 'Errore pubblicazione', type: 'error' })
    } finally {
      setPublishing(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" style={{ color: 'var(--color-text-tertiary)' }}>
        Caricamento builder...
      </div>
    )
  }

  if (!template) {
    return (
      <div className="flex items-center justify-center h-[60vh]" style={{ color: 'var(--color-text-tertiary)' }}>
        Template non trovato
      </div>
    )
  }

  const dragOverlayLabel = activeDragType
    ? FIELD_PALETTE.find(p => p.type === activeDragType)?.label || activeDragType
    : null

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      {/* Toolbar */}
      <div
        className="flex items-center justify-between px-4 py-2 border-b shrink-0"
        style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-card)' }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/configurazione/schede-dinamiche')}
            className="p-1.5 rounded hover:opacity-80 transition-opacity"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-sm font-bold flex items-center gap-2">
              <ClipboardList className="w-4 h-4" style={{ color: 'var(--color-primary)' }} />
              {template.nome}
            </h1>
            <p className="text-[10px]" style={{ color: 'var(--color-text-tertiary)' }}>
              {template.codice} &middot; {fields.length} campi
              {isDirty && ' \u00b7 Modifiche non salvate'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Edit policy toggle */}
          <button
            onClick={() => {
              const next = editPolicy === 'editable' ? 'view_only' : 'editable'
              setEditPolicy(next)
              setIsDirty(true)
            }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors"
            style={{
              borderColor: 'var(--color-border)',
              color: editPolicy === 'view_only' ? 'var(--color-warning, #f59e0b)' : 'var(--color-text-tertiary)',
            }}
            title={editPolicy === 'editable' ? 'Dati modificabili dopo il salvataggio' : 'Dati in sola lettura dopo il salvataggio'}
          >
            {editPolicy === 'view_only' ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
            {editPolicy === 'view_only' ? 'Solo lettura' : 'Modificabile'}
          </button>
          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors disabled:opacity-40"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-primary)' }}
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Salvataggio...' : 'Salva'}
          </button>
          <button
            onClick={handlePublish}
            disabled={fields.length === 0 || publishing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors disabled:opacity-40"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            <Upload className="w-3.5 h-3.5" />
            {publishing ? 'Pubblicazione...' : 'Pubblica'}
          </button>
        </div>
      </div>

      {/* 3-panel layout with shared DndContext */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex flex-1 overflow-hidden">
          {/* Left: Field Palette */}
          <div
            className="w-[220px] shrink-0 border-r overflow-y-auto"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface, var(--color-bg))' }}
          >
            <FieldPalette onAddField={addField} />
          </div>

          {/* Center: Canvas */}
          <div className="flex-1 overflow-y-auto" style={{ backgroundColor: 'var(--color-bg)' }}>
            <BuilderCanvas
              fields={fields}
              selectedFieldId={selectedFieldId}
              onSelectField={setSelectedFieldId}
              onRemoveField={removeField}
            />
          </div>

          {/* Right: Property Panel */}
          <div
            className="w-[300px] shrink-0 border-l overflow-y-auto"
            style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface, var(--color-bg))' }}
          >
            <PropertyPanel
              field={selectedField}
              allFields={fields}
              onUpdate={(updates) => {
                if (selectedFieldId) updateField(selectedFieldId, updates)
              }}
            />
          </div>
        </div>

        {/* Drag overlay — mostra etichetta campo durante il drag dalla palette */}
        <DragOverlay>
          {activeDragType && (
            <div
              className="px-4 py-2 rounded-lg text-xs font-medium shadow-lg border"
              style={{
                backgroundColor: 'var(--color-bg-card)',
                borderColor: 'var(--color-primary)',
                color: 'var(--color-text-primary)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              + {dragOverlayLabel}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  )
}
